import { useEffect, useState } from 'react'
import { CheckCircle2, MessageCircle, RefreshCw, X } from 'lucide-react'

const normalizeInstance = value => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9-]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 50)

const qrSource = value => {
  if (!value) return ''
  return String(value).startsWith('data:image/') ? String(value) : `data:image/png;base64,${value}`
}

export default function WhatsAppSetup({ channel, onAction }) {
  const [instanceName, setInstanceName] = useState(channel?.instance_name || '')
  const [phone, setPhone] = useState(channel?.phone_e164 || '')
  const [result, setResult] = useState(channel || null)
  const [showSetup, setShowSetup] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!channel) return
    setResult(current => ({ ...current, ...channel }))
    setInstanceName(channel.instance_name || '')
    setPhone(channel.phone_e164 || '')
  }, [channel])

  const current = result || channel
  const connected = current?.status === 'connected'
  const waiting = Boolean(current?.instance_name) && !connected

  async function run(action, values = {}) {
    setBusy(true)
    setError('')
    try {
      const response = await onAction({
        action,
        instance_name: current?.instance_name || instanceName,
        phone_e164: phone,
        name: 'WhatsApp principal',
        ...values,
      })
      setResult(previous => ({ ...previous, ...response.channel, qr_code_base64: response.qr_code_base64 || previous?.qr_code_base64 || '' }))
      return response
    } catch (requestError) {
      setError(requestError.message || 'Não foi possível configurar o WhatsApp.')
      throw requestError
    } finally {
      setBusy(false)
    }
  }

  async function create(event) {
    event.preventDefault()
    const normalized = normalizeInstance(instanceName)
    if (!normalized) return setError('Informe um nome válido para a instância.')
    setInstanceName(normalized)
    try {
      await run('create', { instance_name: normalized })
      setShowSetup(true)
    } catch { /* a mensagem já está visível */ }
  }

  useEffect(() => {
    if (!waiting || !showSetup || !current?.instance_name) return undefined
    const timer = window.setInterval(async () => {
      try {
        const response = await onAction({ action: 'status', instance_name: current.instance_name, silent: true })
        setResult(previous => ({ ...previous, ...response.channel, qr_code_base64: previous?.qr_code_base64 || '' }))
      } catch { /* a próxima consulta tenta novamente */ }
    }, 5000)
    return () => window.clearInterval(timer)
  }, [waiting, showSetup, current?.instance_name, onAction])

  return <article className="panel integration-panel whatsapp-setup">
    <div className="panel-title"><div><h2>WhatsApp</h2><p>{current?.instance_name ? `Evolution API · instância ${current.instance_name}` : 'Conecte uma nova instância da Evolution API'}</p></div><MessageCircle /></div>
    {connected ? <div className="whatsapp-connected-card">
      <CheckCircle2 />
      <div><strong>WhatsApp conectado</strong><span>{current.phone_e164 || phone || 'Número confirmado pela Evolution API'}</span></div>
      <button className="secondary" type="button" onClick={() => setShowSetup(true)}>Ver conexão</button>
    </div> : <>
      <p>Crie a instância e conecte o aparelho sem sair do CRM.</p>
      <button className="primary" type="button" onClick={() => setShowSetup(true)}>{waiting ? 'Continuar conexão' : 'Configurar WhatsApp'}</button>
    </>}

    {showSetup && <div className="mini-modal-backdrop" onMouseDown={event => event.target === event.currentTarget && setShowSetup(false)}>
      <section className="mini-modal whatsapp-setup-modal" role="dialog" aria-modal="true" aria-label="Configurar WhatsApp">
        <header><div><span className="eyebrow">Evolution API</span><h2>{connected ? 'WhatsApp conectado' : current?.qr_code_base64 ? 'Leia o QR Code' : 'Conectar WhatsApp'}</h2></div><button className="icon-button" type="button" onClick={() => setShowSetup(false)} aria-label="Fechar"><X /></button></header>
        {connected ? <div className="whatsapp-success">
          <CheckCircle2 />
          <strong>Conexão realizada com sucesso</strong>
          <span>Instância: {current.instance_name}</span>
          <span>Número: {current.phone_e164 || phone || 'confirmado'}</span>
        </div> : current?.qr_code_base64 ? <div className="whatsapp-qr-step">
          <img src={qrSource(current.qr_code_base64)} alt="QR Code para conectar o WhatsApp" />
          <ol><li>Abra o WhatsApp no celular.</li><li>Acesse <strong>Aparelhos conectados</strong>.</li><li>Toque em <strong>Conectar aparelho</strong> e leia o código.</li></ol>
          <p>A tela verificará a conexão automaticamente.</p>
          {error && <div className="form-error">{error}</div>}
          <button className="secondary" type="button" disabled={busy} onClick={() => run('refresh_qr').catch(() => {})}><RefreshCw />{busy ? 'Gerando…' : 'Gerar novo QR Code'}</button>
        </div> : <form className="whatsapp-setup-form" onSubmit={create}>
          <label>Nome da instância<input value={instanceName} onChange={event => setInstanceName(normalizeInstance(event.target.value))} placeholder="empresa-whatsapp" required pattern="[a-z0-9-]+" /></label>
          <small>Use letras minúsculas, números e hífen. Esse nome será único na Evolution.</small>
          <label>Número do WhatsApp<input value={phone} onChange={event => setPhone(event.target.value)} placeholder="+55 21 99999-9999" inputMode="tel" required /></label>
          <small>O número será confirmado depois que o QR Code for lido.</small>
          {error && <div className="form-error">{error}</div>}
          <footer><button className="secondary" type="button" onClick={() => setShowSetup(false)}>Cancelar</button><button className="primary" disabled={busy}>{busy ? 'Criando…' : 'Criar e gerar QR Code'}</button></footer>
        </form>}
      </section>
    </div>}
  </article>
}
